import React from "react";
import {
  Box,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { IConversationFilters } from "../../models/admin/conversationAnalysis/conversationAnalysis";
import { InsureAssistPerformanceStyles } from "./InsureAssistPerformanceStyles";

interface IIAPerformanceFiltersProps {
  filters: IConversationFilters;
  handleDateChange: (type: "from" | "to", value: string) => void;
}

const IAPerformanceFilters: React.FC<IIAPerformanceFiltersProps> = ({
  filters,
  handleDateChange,
}) => {
  const isMobile = useMediaQuery("(max-width:767px)");

  const todayDate = new Date().toISOString().split("T")[0];

  const formatDate = (date: string | Date | undefined) => {
    if (!date) return "";
    if (typeof date === "string") {
      return date.split("T")[0];
    }
    if (date instanceof Date) {
      return date.toISOString().split("T")[0];
    }
    return "";
  };

  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDate = e.target.value;
    if (!selectedDate) {
      handleDateChange("to", "");
      return;
    }
    const date = new Date(selectedDate);
    date.setHours(23, 59, 59, 999);

    const adjustedDate = new Date(
      date.getTime() - date.getTimezoneOffset() * 60000
    );
    const formattedDateTime = adjustedDate.toISOString();
    handleDateChange("to", formattedDateTime);
  };

  return (
    <Box
      sx={{
        ...InsureAssistPerformanceStyles.filterContainer,
        maxWidth: isMobile ? "calc(100vw - 40px)" : "none",
      }}
      id="ia_performance_filters_container"
      data-test-id="ia_performance_filters_container"
      aria-label="Insure Assist Performance Filters"
    >
      <Typography
        variant="h6"
        sx={{
          background: (theme: Theme) =>
            `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          fontWeight: "bold",
        }}
        id="ia_performance_filters_title"
        data-test-id="ia_performance_filters_title"
        aria-label="Insure Assist Performance"
      >
        Insure Assist Performance
      </Typography>
      <Box 
        sx={InsureAssistPerformanceStyles.dateFieldsContianer}
        id="ia_performance_date_fields"
        data-test-id="ia_performance_date_fields"
        aria-label="Date Range Filters"
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
          id="ia_performance_from_date_container"
          data-test-id="ia_performance_from_date_container"
          aria-label="From Date Filter"
        >
          <TextField
            size="small"
            fullWidth
            type="date"
            label="From"
            value={formatDate(filters.fromDate)}
            onChange={(e) => {
              const selectedDate = e.target.value;
              handleDateChange("from", selectedDate);
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: todayDate,
            }}
            id="ia_performance_from_date"
            data-test-id="ia_performance_from_date"
            aria-label="From Date"
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
          id="ia_performance_to_date_container"
          data-test-id="ia_performance_to_date_container"
          aria-label="To Date Filter"
        >
          <TextField
            size="small"
            fullWidth
            type="date"
            label="To"
            value={formatDate(filters.toDate)}
            onChange={handleToDateChange}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: todayDate,
            }}
            id="ia_performance_to_date"
            data-test-id="ia_performance_to_date"
            aria-label="To Date"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default IAPerformanceFilters;
